import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from "../../context/ThemeContext";
import { LuX, LuCalendar } from 'react-icons/lu';

const GradYearModal = ({ isOpen, onClose, onSubmit, currentUser }) => {
  const { theme } = useTheme();
  const [selectedYear, setSelectedYear] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Generate graduation year options (current year + 1 to current year + 6)
  const currentYear = new Date().getFullYear();
  const gradYearOptions = [];
  for (let i = 1; i <= 6; i++) {
    gradYearOptions.push(currentYear + i);
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedYear) return;

    setIsSubmitting(true);
    try {
      await onSubmit(parseInt(selectedYear));
      onClose();
    } catch (error) {
      console.error('Error saving graduation year:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
          className={`${theme.glassCard} shadow-xl max-w-md w-full`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={`${theme.glassHeader} border-b border-white/30 dark:border-white/20 p-6`}>
            <div className="flex justify-between items-start">
              <div>
                <h2 className={`text-xl font-bold ${theme.text.primary} mb-2`}>
                  Welcome to Schedule Builder!
                </h2>
                <p className={`text-sm ${theme.text.secondary}`}>
                  To create your academic plan, we need to know your expected graduation year.
                </p>
              </div>
              <button
                onClick={onClose}
                className={`p-2 hover:bg-white/20 dark:hover:bg-black/20 rounded-lg transition-colors`}
              >
                <LuX className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          <div className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className={`block text-sm font-medium ${theme.text.primary} mb-3`}>
                  Expected Graduation Year
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {gradYearOptions.map((year) => (
                    <button
                      key={year}
                      type="button"
                      onClick={() => setSelectedYear(year.toString())}
                      className={`p-3 rounded-lg border-2 transition-all duration-200 flex items-center justify-center gap-2 ${
                        selectedYear === year.toString()
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                          : `border-gray-200 dark:border-gray-600 ${theme.glassInteractive} hover:border-blue-300`
                      }`}
                    >
                      <LuCalendar className="w-4 h-4" />
                      <span className="font-medium">{year}</span>
                    </button>
                  ))}
                </div>
                {selectedYear && (
                  <motion.p
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`mt-3 text-sm ${theme.text.secondary}`}
                  >
                    You'll see semesters from your first year through {selectedYear}
                  </motion.p>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className={`flex-1 px-4 py-2 text-sm font-medium ${theme.glassInteractive} rounded-lg transition-colors`}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!selectedYear || isSubmitting}
                  className={`flex-1 px-4 py-2 text-sm font-medium bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {isSubmitting ? 'Saving...' : 'Continue'}
                </button>
              </div>
            </form>

            <div className={`mt-6 p-4 ${theme.glassCard} rounded-lg`}>
              <h4 className={`text-sm font-medium ${theme.text.primary} mb-2`}>
                Why do we need this?
              </h4>
              <ul className={`text-xs ${theme.text.secondary} space-y-1`}>
                <li>• Show you all relevant semesters for planning</li>
                <li>• Track your progress toward graduation</li>
                <li>• Help with distribution requirements</li>
                <li>• Plan course sequences over multiple years</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default GradYearModal;